import { create } from 'zustand';
import type { User, ServerWatchlist, ServerTemplate, PriceAdjustmentType, CupHandleSettings, WilliamOneilSellSettings } from '@/types';
import { useChartStore } from '@/store/chartStore';
import { useAlertStore } from '@/store/alertStore';

interface AuthState {
  user: User | null;
  loading: boolean;

  // Server-stored data (only fetched for logged-in users)
  serverWatchlists: ServerWatchlist[];
  serverTemplates: ServerTemplate[];

  // Actions
  fetchMe: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setServerWatchlists: (lists: ServerWatchlist[]) => void;
  setServerTemplates: (templates: ServerTemplate[]) => void;

  fetchWatchlists: () => Promise<void>;
  fetchTemplates: () => Promise<void>;
  saveWatchlist: (name: string, symbols: unknown[]) => Promise<ServerWatchlist | null>;
  updateWatchlist: (id: string, name: string, symbols: unknown[]) => Promise<void>;
  deleteWatchlist: (id: string) => Promise<void>;
  saveTemplate: (name: string, template: unknown) => Promise<ServerTemplate | null>;
  updateTemplate: (id: string, name: string, template: unknown) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;

  // Upgrade modal
  upgradeModalOpen: boolean;
  openUpgradeModal:  () => void;
  closeUpgradeModal: () => void;

  // Profile settings modal
  profileSettingsOpen: boolean;
  profileSettingsTab: 'profile' | 'account' | 'plan' | 'alerts' | 'developer' | 'danger';
  openProfileSettings: (tab?: 'profile' | 'account' | 'plan' | 'alerts' | 'developer' | 'danger') => void;
  closeProfileSettings: () => void;

  // Profile actions
  updateProfile: (name: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<string>;
  changeEmail: (newEmail: string, password?: string) => Promise<void>;
  cancelEmailChange: () => Promise<void>;
  deleteAccount: (confirmation: string, password?: string) => Promise<void>;
  linkTelegram: () => Promise<{ url: string }>;
  unlinkTelegram: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user:             null,
  loading:          true,
  serverWatchlists: [],
  serverTemplates:  [],
  upgradeModalOpen: false,
  profileSettingsOpen: false,
  profileSettingsTab: 'profile',

  openUpgradeModal:  () => set({ upgradeModalOpen: true }),
  closeUpgradeModal: () => set({ upgradeModalOpen: false }),

  openProfileSettings: (tab = 'profile') => set({ profileSettingsOpen: true, profileSettingsTab: tab }),
  closeProfileSettings: () => set({ profileSettingsOpen: false }),

  setUser: (user) => set({ user }),
  setServerWatchlists: (lists) => set({ serverWatchlists: lists }),
  setServerTemplates: (templates) => set({ serverTemplates: templates }),

  fetchMe: async () => {
    set({ loading: true });
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const { user } = (await res.json()) as { user: User | null };
        // 200 with no user means nobody is signed in (an anonymous visitor): nothing to load.
        if (!user) {
          set({ user: null });
          return;
        }
        set({ user });
        // Fetch associated data in parallel
        await Promise.all([get().fetchWatchlists(), get().fetchTemplates()]);
        // Load favorite indicators from server (chart settings now come from templates)
        try {
          const settingsRes = await fetch('/api/user/settings', { credentials: 'include' });
          if (settingsRes.ok) {
            const settings = (await settingsRes.json()) as {
              favoriteIndicators?: string[];
              chartSettings?: {
                priceAdjustments?: string[];
                cupHandleSettings?: Partial<CupHandleSettings>;
                williamOneilSellSettings?: Partial<WilliamOneilSellSettings>;
              };
            };
            if (Array.isArray(settings.favoriteIndicators)) {
              useChartStore.setState({ favoriteIndicatorNames: settings.favoriteIndicators });
            }
            const validAdjustments: PriceAdjustmentType[] = ['splits', 'div', 'div_script', 'rights'];
            const savedAdjustments = settings.chartSettings?.priceAdjustments;
            if (Array.isArray(savedAdjustments)) {
              const filtered = savedAdjustments.filter((v): v is PriceAdjustmentType =>
                (validAdjustments as string[]).includes(v)
              );
              if (filtered.length) useChartStore.setState({ priceAdjustments: filtered });
            }
            const savedCupHandle = settings.chartSettings?.cupHandleSettings;
            if (savedCupHandle && typeof savedCupHandle === 'object') {
              useChartStore.setState((s) => ({ cupHandleSettings: { ...s.cupHandleSettings, ...savedCupHandle } }));
            }
            const savedOneilSell = settings.chartSettings?.williamOneilSellSettings;
            if (savedOneilSell && typeof savedOneilSell === 'object') {
              useChartStore.setState((s) => ({
                williamOneilSellSettings: {
                  ma50Break:        { ...s.williamOneilSellSettings.ma50Break,        ...savedOneilSell.ma50Break },
                  ma200Break:       { ...s.williamOneilSellSettings.ma200Break,       ...savedOneilSell.ma200Break },
                  climaxTop:        { ...s.williamOneilSellSettings.climaxTop,        ...savedOneilSell.climaxTop },
                  exhaustionGap:    { ...s.williamOneilSellSettings.exhaustionGap,    ...savedOneilSell.exhaustionGap },
                  distributionDays: { ...s.williamOneilSellSettings.distributionDays, ...savedOneilSell.distributionDays },
                  pivotStop:        { ...s.williamOneilSellSettings.pivotStop,        ...savedOneilSell.pivotStop },
                },
              }));
            }
          }
        } catch { /* silent — user may not have settings yet */ }
        // Auto-create default template if user has none
        try {
          const templatesRes = await fetch('/api/user/templates', { credentials: 'include' });
          if (templatesRes.ok) {
            const { templates } = (await templatesRes.json()) as { templates: ServerTemplate[] };
            if (templates.length === 0) {
              await fetch('/api/user/templates', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: 'Default',
                  template: {
                    version: 1,
                    indicators: [{ name: 'VOL' }],
                    chartSettings: {
                      candleUpColor: '#26a69a',
                      candleDownColor: '#ef5350',
                      hlcUpColor: '#22C55E',
                      hlcDownColor: '#EF4444',
                      showHGrid: true,
                      showVGrid: true,
                      showPriceMarkHigh: true,
                      showPriceMarkLow: true,
                      showLastPriceLine: true,
                      showCrosshair: true,
                      yAxisReverse: false,
                      priceScaleType: 'normal',
                    },
                  },
                }),
              });
              // Refresh templates list
              await get().fetchTemplates();
            }
          }
        } catch { /* silent */ }
      } else {
        set({ user: null });
      }
    } catch {
      set({ user: null });
    } finally {
      set({ loading: false });
    }
  },

  logout: async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    useChartStore.getState().resetForLogout();
    useAlertStore.getState().resetForLogout();
    set({ user: null, serverWatchlists: [], serverTemplates: [] });
  },

  fetchWatchlists: async () => {
    const { user } = get();
    if (!user) return;
    try {
      const res = await fetch('/api/user/watchlists', { credentials: 'include' });
      if (res.ok) {
        const { watchlists } = (await res.json()) as { watchlists: ServerWatchlist[] };
        set({ serverWatchlists: watchlists });
      }
    } catch { /* silent */ }
  },

  fetchTemplates: async () => {
    const { user } = get();
    if (!user) return;
    try {
      const res = await fetch('/api/user/templates', { credentials: 'include' });
      if (res.ok) {
        const { templates } = (await res.json()) as { templates: ServerTemplate[] };
        set({ serverTemplates: templates });
      }
    } catch { /* silent */ }
  },

  saveWatchlist: async (name, symbols) => {
    const res = await fetch('/api/user/watchlists', {
      method:  'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, symbols }),
    });
    if (!res.ok) {
      const { error } = (await res.json()) as { error: string };
      throw new Error(error);
    }
    const created = (await res.json()) as ServerWatchlist;
    set((s) => ({ serverWatchlists: [...s.serverWatchlists, created] }));
    return created;
  },

  updateWatchlist: async (id, name, symbols) => {
    const res = await fetch(`/api/user/watchlists/${id}`, {
      method:  'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, symbols }),
    });
    if (!res.ok) {
      const { error } = (await res.json()) as { error: string };
      throw new Error(error);
    }
    await get().fetchWatchlists();
  },

  deleteWatchlist: async (id) => {
    await fetch(`/api/user/watchlists/${id}`, { method: 'DELETE', credentials: 'include' });
    set((s) => ({ serverWatchlists: s.serverWatchlists.filter((w) => w.id !== id) }));
  },

  saveTemplate: async (name, template) => {
    const res = await fetch('/api/user/templates', {
      method:  'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, template }),
    });
    if (!res.ok) {
      const { error } = (await res.json()) as { error: string };
      throw new Error(error);
    }
    const created = (await res.json()) as ServerTemplate;
    set((s) => ({ serverTemplates: [...s.serverTemplates, created] }));
    return created;
  },

  updateTemplate: async (id, name, template) => {
    const res = await fetch(`/api/user/templates/${id}`, {
      method:  'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, template }),
    });
    if (!res.ok) {
      const { error } = (await res.json()) as { error: string };
      throw new Error(error);
    }
    await get().fetchTemplates();
  },

  deleteTemplate: async (id) => {
    await fetch(`/api/user/templates/${id}`, { method: 'DELETE', credentials: 'include' });
    set((s) => ({ serverTemplates: s.serverTemplates.filter((t) => t.id !== id) }));
  },

  // ─── Profile actions ───────────────────────────────────────────────────────

  updateProfile: async (name: string) => {
    const res = await fetch('/api/user/profile', {
      method:      'PUT',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ name }),
    });
    if (!res.ok) {
      const j = (await res.json()) as { error?: string };
      throw new Error(j.error ?? 'Failed to update profile');
    }
    // Optimistically update name in store
    set((s) => ({ user: s.user ? { ...s.user, name } : null }));
  },

  uploadAvatar: async (file: File) => {
    const form = new FormData();
    form.append('avatar', file);
    const res = await fetch('/api/user/avatar', {
      method:      'POST',
      credentials: 'include',
      body:        form,
    });
    if (!res.ok) {
      const j = (await res.json()) as { error?: string };
      throw new Error(j.error ?? 'Upload failed');
    }
    const { avatarUrl } = (await res.json()) as { avatarUrl: string };
    // Append a cache-bust timestamp so browsers re-fetch the new image.
    // The backend now returns ?v=updated_at, so we must use &t=… when a query string exists.
    const sep = avatarUrl.includes('?') ? '&' : '?';
    const busted = `${avatarUrl}${sep}t=${Date.now()}`;
    set((s) => ({ user: s.user ? { ...s.user, avatarUrl: busted } : null }));
    return busted;
  },

  changeEmail: async (newEmail: string, password?: string) => {
    const res = await fetch('/api/user/change-email', {
      method:      'POST',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ newEmail, password }),
    });
    if (!res.ok) {
      const j = (await res.json()) as { error?: string };
      throw new Error(j.error ?? 'Failed to request email change');
    }
    set((s) => ({ user: s.user ? { ...s.user, pendingEmail: newEmail } : null }));
  },

  cancelEmailChange: async () => {
    const res = await fetch('/api/user/change-email', {
      method:      'DELETE',
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to cancel email change');
    set((s) => ({ user: s.user ? { ...s.user, pendingEmail: null } : null }));
  },

  deleteAccount: async (confirmation: string, password?: string) => {
    const res = await fetch('/api/user/account', {
      method:      'DELETE',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ confirmation, password }),
    });
    if (!res.ok) {
      const j = (await res.json()) as { error?: string };
      throw new Error(j.error ?? 'Failed to delete account');
    }
    // Clear local state
    useChartStore.getState().resetForLogout();
    useAlertStore.getState().resetForLogout();
    set({ user: null, serverWatchlists: [], serverTemplates: [] });
  },

  linkTelegram: async () => {
    const res = await fetch('/api/user/link-telegram', {
      method:      'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      const j = (await res.json()) as { error?: string };
      throw new Error(j.error ?? 'Failed to generate Telegram link');
    }
    return (await res.json()) as { url: string };
  },

  unlinkTelegram: async () => {
    const res = await fetch('/api/user/link-telegram', {
      method:      'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      const j = (await res.json()) as { error?: string };
      throw new Error(j.error ?? 'Failed to disconnect Telegram');
    }
    set((s) => ({ user: s.user ? { ...s.user, telegramChatId: null } : null }));
  },
}));

/**
 * Resolves once we know who the user is (signed in, or the sign-in check has
 * finished). Anything that gates on the user's plan at page load must wait for
 * this — otherwise a Pro user is briefly indistinguishable from a guest.
 * Gives up after `timeoutMs` so a hung request can't block the caller forever.
 */
export function waitForAuthSettled(timeoutMs = 10_000): Promise<void> {
  const settled = () => {
    const { user, loading } = useAuthStore.getState();
    return !!user || !loading;
  };
  if (settled()) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = useAuthStore.subscribe(() => {
      if (!settled()) return;
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
    const timer = setTimeout(() => { unsubscribe(); resolve(); }, timeoutMs);
  });
}
